/**
 * Creates a handler that waits for images to load before adjusting DataTable columns
 * @param {Object} options - Configuration options
 * @param {number} [options.fallbackTimeout=3000] - Timeout in ms before forcing column adjustment
 * @param {boolean} [options.debug=false] - Whether to log debug information
 * @returns {Function} - Function to be passed to DataTable's draw event
 */
export const createTableImageLoadHandler = (options = {}) => {
  const { fallbackTimeout = 3000 } = options;

  /**
   * The actual handler function to be passed to DataTable's .on('draw') method
   * @param {Object} dataTable - The DataTable instance
   * @returns {void}
   */
  return function handleTableDraw(dataTable) {
    // Find all images in the table
    const tableElement = dataTable.table().node();
    const images = tableElement.querySelectorAll("img");

    // If there are no images, adjust immediately
    if (images.length === 0) {
      dataTable.columns.adjust();
      return;
    }

    // Counter to track loaded images
    let loadedImages = 0;
    const totalImages = images.length;

    // Function to adjust columns once all images are loaded
    const handleImageLoad = () => {
      loadedImages++;

      if (loadedImages === totalImages) {
        dataTable.columns.adjust();

        // Clean up all event listeners
        cleanupEventListeners();
      }
    };

    // Create a cleanup function to remove all event listeners
    const cleanupEventListeners = () => {
      images.forEach((img) => {
        img.removeEventListener("load", handleImageLoad);
        img.removeEventListener("error", handleImageLoad);
      });
    };

    // Set up load event listeners for each image
    images.forEach((img) => {
      // If image is already loaded (from cache)
      if (img.complete) {
        handleImageLoad();
      } else {
        // Handle successfully loaded images
        img.addEventListener("load", handleImageLoad);

        // Handle failed images too
        img.addEventListener("error", handleImageLoad);
      }
    });

    // Fallback timeout in case some images never load
    const fallbackTimeoutId = setTimeout(() => {
      if (loadedImages < totalImages) {
        dataTable.columns.adjust();

        // Clean up all event listeners when fallback is triggered
        cleanupEventListeners();
      }

      // Clear the timeout reference
      clearTimeout(fallbackTimeoutId);
    }, fallbackTimeout);

    // Also handle cleanup if table is destroyed
    dataTable.one("destroy", () => {
      cleanupEventListeners();
      clearTimeout(fallbackTimeoutId);
    });
  };
};

/**
 * Applies the image load handler directly to a DataTable instance
 * @param {Object} dataTable - The DataTable instance
 * @param {Object} [options] - Configuration options (see createTableImageLoadHandler)
 * @returns {Object} - The DataTable instance (for chaining)
 */
export const applyImageLoadHandler = (dataTable, options = {}) => {
  const handler = createTableImageLoadHandler(options);

  // Use bind to ensure 'this' context is correct when the handler is called
  dataTable.on("draw", function () {
    handler(dataTable);
  });

  return dataTable;
};
